/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
/**
 * ???????????
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { getConditions, getCondition, createCondition, updateCondition, deleteCondition, getAvatars, exportConditionsToCsv, importConditionsFromCsv } from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Loader2, FileText, FileCheck, X, Download, Upload, Copy } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { Badge } from '../../components/ui/badge';
import { getLegacyCategoryTable } from '../../lib/conditionTableNormalizer';
import {
  applyCellGridEditsToV3ConditionData,
  conditionDataToCellGrid,
  hasConditionCategoryGrid,
} from '../../lib/conditionDataToCells';
import {
  Complaint1Section,
  Complaint2Section,
  Complaint3Section,
  Complaint4Section,
  Complaint5Section,
  Complaint6Section,
} from './QuestionnaireFormSections';

function CellTestPanel({ onEditCondition }: { onEditCondition?: (conditionId: number) => void }) {
  const [testConditionId, setTestConditionId] = useState('');
  const [testCatId, setTestCatId] = useState('');
  const [testLv, setTestLv] = useState('Lv1');
  const [questionLoading, setQuestionLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [generatedQuestion, setGeneratedQuestion] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [categoryKeys, setCategoryKeys] = useState<Array<{ id: string; label: string }>>([]);
  const [categoryTableData, setCategoryTableData] = useState<Record<string, Record<string, string>>>({});

  useEffect(() => {
    if (!testConditionId) { setCategoryKeys([]); setCategoryTableData({}); setTestCatId(''); return; }
    const fetchKeys = async () => {
      try {
        const res = await fetch(`/api/admin/conditions/${testConditionId}`, { credentials: 'include' });
        if (!res.ok) { setCategoryKeys([]); setCategoryTableData({}); return; }
        const data = await res.json();
        const table = getLegacyCategoryTable(data?.condition?.condition_data);
        if (!table) { setCategoryKeys([]); setCategoryTableData({}); return; }
        setCategoryTableData(table);
        const keys = Object.entries(table)
          .sort(([a], [b]) => {
            const na = parseInt((a.match(/\d+/) || ['99'])[0]);
            const nb = parseInt((b.match(/\d+/) || ['99'])[0]);
            return na !== nb ? na - nb : a.localeCompare(b);
          })
          .map(([id, val]: [string, any]) => ({
            id,
            label: val?.category ? `${id} ${val.category}` : id,
          }));
        setCategoryKeys(keys);
        if (keys.length > 0 && !keys.find(k => k.id === testCatId)) {
          setTestCatId(keys[0].id);
        }
      } catch { setCategoryKeys([]); setCategoryTableData({}); }
    };
    const timer = setTimeout(fetchKeys, 300);
    return () => clearTimeout(timer);
  }, [testConditionId]);

  const cellValue = testCatId && testLv ? (categoryTableData[testCatId]?.[testLv] || '') : '';

  // カテゴリ/Lvが変わったらリセット
  useEffect(() => {
    setGeneratedQuestion('');
    setTestResult(null);
  }, [testCatId, testLv]);

  const generateQuestion = async () => {
    if (!testConditionId || !testCatId) return;
    setQuestionLoading(true);
    setGeneratedQuestion('');
    setTestResult(null);
    try {
      const res = await fetch('/api/admin/test-cell/generate-question', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ conditionId: parseInt(testConditionId), catId: testCatId, lv: testLv }),
      });
      const data = await res.json();
      if (data.status === 'skip') {
        setTestResult(data);
      } else {
        setGeneratedQuestion(data.question || '');
      }
    } catch (e: any) {
      setTestResult({ status: 'error', message: e.message });
    }
    setQuestionLoading(false);
  };

  const runTest = async () => {
    if (!testConditionId || !testCatId || !generatedQuestion) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await fetch('/api/admin/test-cell/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ conditionId: parseInt(testConditionId), catId: testCatId, lv: testLv, question: generatedQuestion }),
      });
      setTestResult(await res.json());
    } catch (e: any) {
      setTestResult({ status: 'error', message: e.message });
    }
    setTestLoading(false);
  };

  const statusLabel = (s: string) =>
    s === 'pass' ? '✅ pass' : s === 'mismatch' ? '⚠️ ズレ' : s === 'skip' ? '⏭ スキップ' : `❌ ${s}`;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4">
        <div className="text-2xl font-bold text-gray-900">セルテスト</div>
        <p className="text-base text-gray-500 mt-1">IDとC×Lvを指定して患者応答・スロット抽出をテスト</p>
      </div>
      <div>
        {/* Step 1: 条件・カテゴリ・レベル選択 + 質問生成 */}
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <Label className="text-base font-bold text-gray-700">ID</Label>
            <Input type="number" className="w-24" placeholder="75" value={testConditionId} onChange={(e) => setTestConditionId(e.target.value)} />
          </div>
          <div className="min-w-[200px]">
            <Label className="text-base font-bold text-gray-700">カテゴリ</Label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={testCatId}
              onChange={(e) => setTestCatId(e.target.value)} disabled={categoryKeys.length === 0}>
              {categoryKeys.length === 0 && <option value="">IDを入力</option>}
              {categoryKeys.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </div>
          <div className="min-w-[200px]">
            <Label className="text-base font-bold text-gray-700">レベル</Label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={testLv} onChange={(e) => setTestLv(e.target.value)}>
              <option value="Lv1">Lv1 有無・事実</option>
              <option value="Lv2">Lv2 頻度・期間・数量</option>
              <option value="Lv3">Lv3 方法・具体</option>
              <option value="Lv4">Lv4 理由・背景</option>
              <option value="Lv5">Lv5 工夫・調整</option>
              <option value="Lv6">Lv6 行動変容の意志確認</option>
            </select>
          </div>
          <Button onClick={generateQuestion} disabled={questionLoading || !testConditionId || !testCatId} className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
            {questionLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />生成中...</> : '質問生成'}
          </Button>
        </div>

        {/* 期待値プレビュー */}
        {testCatId && (
          <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
            <span className="text-gray-500 font-semibold">{testCatId}.{testLv} 期待値: </span>
            {cellValue ? <span>{cellValue}</span> : <span className="text-gray-400">（空セル）</span>}
          </div>
        )}

        {/* Step 2: 生成された質問 + テスト実行 */}
        {generatedQuestion && (
          <div className="mt-3 flex gap-2 items-start">
            <div className="flex-1">
              <Label className="text-xs text-gray-500">質問</Label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                rows={2}
                value={generatedQuestion}
                onChange={(e) => setGeneratedQuestion(e.target.value)}
              />
            </div>
            <Button onClick={runTest} disabled={testLoading || !generatedQuestion} className="mt-5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
              {testLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />テスト中...</> : 'テスト実行'}
            </Button>
          </div>
        )}

        {/* 結果表示 */}
        {testResult && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm space-y-2">
            <div className="font-semibold text-base">{testResult.catId}.{testResult.lv} [{statusLabel(testResult.status)}]</div>
            {testResult.patientResponse && (
              <div><span className="text-gray-500 font-semibold">応答:</span> {testResult.patientResponse}</div>
            )}
            {testResult.extractedItems?.length > 0 && (
              <div>
                <span className="text-gray-500 font-semibold">抽出: </span>
                {testResult.extractedItems.map((i: any, idx: number) => (
                  <span key={idx} className={`inline-block mr-2 px-2 py-0.5 rounded text-xs ${
                    i.catId === testResult.catId && i.lv === testResult.lv ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {i.catId}.{i.lv}
                  </span>
                ))}
              </div>
            )}
            {testResult.message && <div className="text-gray-500">{testResult.message}</div>}
            {onEditCondition && testConditionId && (
              <div className="pt-2 border-t mt-2">
                <Button variant="outline" size="sm" onClick={() => onEditCondition(parseInt(testConditionId))}>
                  <Edit className="w-4 h-4 mr-1" />
                  このIDを編集
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryTestPanel({ onEditCondition }: { onEditCondition?: (conditionId: number) => void }) {
  const [testConditionId, setTestConditionId] = useState('');
  const [testCatId, setTestCatId] = useState('');
  const [questionLoading, setQuestionLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<Array<{ lv: string; value: string; question: string }>>([]);
  const [results, setResults] = useState<any>(null);
  const [categoryKeys, setCategoryKeys] = useState<Array<{ id: string; label: string }>>([]);

  useEffect(() => {
    if (!testConditionId) { setCategoryKeys([]); setTestCatId(''); return; }
    const fetchKeys = async () => {
      try {
        const res = await fetch(`/api/admin/conditions/${testConditionId}`, { credentials: 'include' });
        if (!res.ok) { setCategoryKeys([]); return; }
        const data = await res.json();
        const table = getLegacyCategoryTable(data?.condition?.condition_data);
        if (!table) { setCategoryKeys([]); return; }
        const keys = Object.entries(table)
          .sort(([a], [b]) => {
            const na = parseInt((a.match(/\d+/) || ['99'])[0]);
            const nb = parseInt((b.match(/\d+/) || ['99'])[0]);
            return na !== nb ? na - nb : a.localeCompare(b);
          })
          .map(([id, val]: [string, any]) => ({
            id,
            label: val?.category ? `${id} ${val.category}` : id,
          }));
        setCategoryKeys(keys);
        if (keys.length > 0 && !keys.find(k => k.id === testCatId)) {
          setTestCatId(keys[0].id);
        }
      } catch { setCategoryKeys([]); }
    };
    const timer = setTimeout(fetchKeys, 300);
    return () => clearTimeout(timer);
  }, [testConditionId]);

  useEffect(() => {
    setGeneratedQuestions([]);
    setResults(null);
  }, [testCatId]);

  const generateQuestions = async () => {
    if (!testConditionId || !testCatId) return;
    setQuestionLoading(true);
    setGeneratedQuestions([]);
    setResults(null);
    try {
      const res = await fetch('/api/admin/test-cell/generate-category-questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ conditionId: parseInt(testConditionId), catId: testCatId }),
      });
      const data = await res.json();
      if (data.questions) {
        setGeneratedQuestions(data.questions);
      } else if (data.message) {
        setResults({ message: data.message });
      }
    } catch (e: any) {
      setResults({ error: e.message });
    }
    setQuestionLoading(false);
  };

  const updateQuestion = (idx: number, question: string) => {
    setGeneratedQuestions(prev => prev.map((q, i) => i === idx ? { ...q, question } : q));
  };

  const runCategoryTest = async () => {
    if (!testConditionId || !testCatId || generatedQuestions.length === 0) return;
    setTestLoading(true);
    setResults(null);
    try {
      const res = await fetch('/api/admin/test-cell/run-category', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          conditionId: parseInt(testConditionId),
          catId: testCatId,
          questions: generatedQuestions.map(q => ({ lv: q.lv, question: q.question })),
        }),
      });
      setResults(await res.json());
    } catch (e: any) {
      setResults({ error: e.message });
    }
    setTestLoading(false);
  };

  const lvLabels: Record<string, string> = {
    Lv1: '有無・事実', Lv2: '頻度・期間・数量', Lv3: '方法・具体',
    Lv4: '理由・背景', Lv5: '工夫・調整', Lv6: '行動変容',
  };

  const statusLabel = (s: string) =>
    s === 'pass' ? '✅' : s === 'mismatch' ? '⚠️' : s === 'no_response' ? '🔇' : '❌';

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4">
        <div className="text-2xl font-bold text-gray-900">カテゴリ一括テスト</div>
        <p className="text-base text-gray-500 mt-1">カテゴリ内の全Lvを順にテスト（質問生成→編集→テスト実行）</p>
      </div>
      <div>
        {/* Step 1: ID・カテゴリ選択 + 質問生成 */}
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <Label className="text-base font-bold text-gray-700">ID</Label>
            <Input type="number" className="w-24" placeholder="75" value={testConditionId} onChange={(e) => setTestConditionId(e.target.value)} />
          </div>
          <div className="min-w-[200px]">
            <Label className="text-base font-bold text-gray-700">カテゴリ</Label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={testCatId}
              onChange={(e) => setTestCatId(e.target.value)} disabled={categoryKeys.length === 0}>
              {categoryKeys.length === 0 && <option value="">IDを入力</option>}
              {categoryKeys.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </div>
          <Button onClick={generateQuestions} disabled={questionLoading || !testConditionId || !testCatId} className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
            {questionLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />生成中...</> : '質問生成'}
          </Button>
        </div>

        {/* Step 2: 生成された質問一覧（編集可能）+ テスト実行 */}
        {generatedQuestions.length > 0 && (
          <div className="mt-4 space-y-2">
            {generatedQuestions.map((q, idx) => (
              <div key={idx} className="space-y-1">
                <div className="text-sm font-semibold">{q.lv} {lvLabels[q.lv] || ''} — 期待値: {q.value}</div>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  rows={1}
                  value={q.question}
                  onChange={(e) => updateQuestion(idx, e.target.value)}
                />
              </div>
            ))}
            <Button onClick={runCategoryTest} disabled={testLoading} className="mt-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
              {testLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />テスト中...</> : '一括テスト実行'}
            </Button>
          </div>
        )}

        {/* 結果表示 */}
        {results && !results.error && results.results && (
          <div className="mt-4 space-y-3">
            <div className="flex gap-4 items-center text-sm">
              <span className="font-semibold">{results.catId}</span>
              <span>合計: {results.total}セル</span>
              <span className="text-green-700 font-semibold">✅ {results.pass}</span>
              {results.total - results.pass > 0 && (
                <span className="text-red-600 font-semibold">不合格: {results.total - results.pass}</span>
              )}
            </div>

            {results.results.map((r: any, idx: number) => (
              <div key={idx} className={`p-3 rounded-lg text-sm border ${
                r.status === 'pass' ? 'bg-green-50 border-green-200' :
                r.status === 'mismatch' ? 'bg-yellow-50 border-yellow-200' :
                'bg-red-50 border-red-200'
              }`}>
                <div className="font-semibold mb-1">{statusLabel(r.status)} {results.catId}.{r.lv} — 期待値: {r.value}</div>
                <div className="text-gray-600"><span className="font-semibold">質問:</span> {r.question}</div>
                <div className="text-gray-600"><span className="font-semibold">応答:</span> {r.patientResponse}</div>
                {r.extractedItems?.length > 0 && (
                  <div className="mt-1">
                    <span className="text-gray-500 font-semibold">抽出: </span>
                    {r.extractedItems.map((i: any, iIdx: number) => (
                      <span key={iIdx} className={`inline-block mr-2 px-2 py-0.5 rounded text-xs ${
                        i.catId === results.catId && i.lv === r.lv ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {i.catId}.{i.lv}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {onEditCondition && testConditionId && (
              <div className="pt-2 border-t mt-2">
                <Button variant="outline" size="sm" onClick={() => onEditCondition(parseInt(testConditionId))}>
                  <Edit className="w-4 h-4 mr-1" />
                  このIDを編集
                </Button>
              </div>
            )}
          </div>
        )}

        {results?.error && (
          <div className="mt-3 p-3 bg-red-50 rounded text-sm text-red-700">{results.error}</div>
        )}

        {results?.message && !results.results && (
          <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-500">{results.message}</div>
        )}
      </div>
    </div>
  );
}

// タブごとの項目定義
interface ConditionFormItem {
  key: string;
  label: string;
  placeholder: string;
  disabled?: boolean; // ????????????????????????
}

interface ConditionFormData {
  [key: string]: string;
}

interface QuestionnaireFormData {
  [key: string]: string | string[] | boolean;
}

// ?????????
const conditionFormTabs: Record<string, ConditionFormItem[]> = {
  'dental-history': [
    { key: '定期検診は受けているか？', label: '定期検診は受けているか？', placeholder: '歯科クリニックで行っています。' },
    { key: '定期検診の間隔は？', label: '定期検診の間隔は？', placeholder: '6ヶ月に1回程度' },
    { key: '定期検診の前回受診日は？', label: '定期検診の前回受診日は？', placeholder: '3ヶ月前' },
    { key: '口腔衛生指導の経験は？', label: '口腔衛生指導の経験は？', placeholder: '口腔衛生指導を受けたことがあります。', disabled: true },
    { key: 'いつ，誰から口腔衛生指導を受けたか？', label: 'いつ，誰から口腔衛生指導を受けたか？', placeholder: '口腔衛生指導を６ヶ月前、定期検診の時に歯科衛生士さんから受けました。' },
    { key: '口腔衛生指導を受けた内容は？', label: '口腔衛生指導を受けた内容は？', placeholder: '歯磨き時のブラッシングの仕方、歯間ブラシの使い方の方法を教えてもらいました。' },
    { key: 'クリーニングの経験は？', label: 'クリーニングの経験は？', placeholder: 'あります。' },
    { key: 'クリーニングは前回いつ受けたか？', label: 'クリーニングは前回いつ受けたか？', placeholder: 'クリーニングは、検診と同時に行ってもらっている。前回は歯石の除去を中心に行ってもらいました。' },
    { key: 'クリーニング時の音や水での不快感な点はあったか？', label: 'クリーニング時の音や水での不快感な点はあったか？', placeholder: '機会で歯石を除去する時の甲高い音が気になりました。' },
    { key: '抜歯の経験または麻酔の経験は？', label: '抜歯の経験または麻酔の経験は？', placeholder: 'はい、あります。' },
    { key: '抜歯または麻酔の時にどの様な状態だったか？', label: '抜歯または麻酔の時にどの様な状態だったか？', placeholder: '４年前に親知らずの抜歯を大学病院で行った。麻酔もそのときに行いました。' },
    { key: '抜歯または麻酔の時に気分が悪くなったことがあるか？', label: '抜歯または麻酔の時に気分が悪くなったことがあるか？', placeholder: 'はい、あります。', disabled: true },
    { key: '抜歯または麻酔の時に気分が悪くなったのはどの様な状態？', label: '抜歯または麻酔の時に気分が悪くなったのはどの様な状態？', placeholder: '麻酔の味が苦くて、気持ち悪くなったことがありましたが、すぐに良くなりました。' },
    { key: '抜歯または麻酔の時に血が止まりにくいことがあるか？', label: '抜歯または麻酔の時に血が止まりにくいことがあるか？', placeholder: '血が止まりにくいことは、特にないです。' },
    { key: '普段，ケガをした際に血が止まりにくかったことはないか？', label: '普段，ケガをした際に血が止まりにくかったことはないか？', placeholder: '血が止まりにくいことは、特にないです。' },
    { key: '歯肉からの出血の有無と頻度は？', label: '歯肉からの出血の有無と頻度は？', placeholder: '1ｹ月に一度程度、疲れていると出血することがあります。' },
    { key: '現在，口の中で気になることは？', label: '現在，口の中で気になることは？', placeholder: 'はい、あります。', disabled: true },
    { key: '現在，口の中でどこが気になるか（歯並び，歯の色，顎関節症，口臭）', label: '現在，口の中でどこが気になるか（歯並び，歯の色，顎関節症，口臭）', placeholder: '歯並びが悪いので、歯ブラシが届きにくい場所があります。', disabled: true },
    { key: '口の中でどこが気になり始めたのはいつから？', label: '口の中でどこが気になり始めたのはいつから？', placeholder: '子供の頃からずっと感じています。' },
    { key: '現在の口の中の不安はある？', label: '現在の口の中の不安はある？', placeholder: 'はい、あります。' },
    { key: '現在の口の中で何が不安？', label: '現在の口の中で何が不安？', placeholder: '歯並びが悪いので、歯磨きがし易くなる様に、矯正をしてブラッシングをし易くした方が良いのではないかと悩んでいます。' },
  ],
  'lifestyle': [
    { key: '間食はするか？', label: '間食はするか？', placeholder: '間食はしない。', disabled: true },
    { key: '間食はいつする？', label: '間食はいつする？', placeholder: '間食はしない。' },
    { key: '間食はどのようなものを，どれくらい食べるか？', label: '間食はどのようなものを，どれくらい食べるか？', placeholder: '間食はしない。' },
    { key: '食べた後に口をゆすぐなど何かしているか？', label: '食べた後に口をゆすぐなど何かしているか？', placeholder: '何か食べた後に、口をゆすぐ心がけはしている。' },
    { key: 'どの様に、食べた後に口をゆすぐのか？', label: 'どの様に、食べた後に口をゆすぐのか？', placeholder: '仕事中にペットボトルの無糖のお茶をいつも飲んでいるので、食べた後はゆすぎながら飲んでいる。' },
    { key: '口をゆすがない理由は？', label: '口をゆすがない理由は？', placeholder: '口をゆすぐようにしている。' },
    { key: '酸性の飲み物や食べ物を食べるか？', label: '酸性の飲み物や食べ物を食べるか？', placeholder: 'はい、飲んでいます。' },
    { key: 'どのようなものを，どの程度摂取するのか？', label: 'どのようなものを，どの程度摂取するのか？', placeholder: 'オレンジジュースが好きで、時々飲んでいる。' },
    { key: 'アルコール飲料は飲むか？', label: 'アルコール飲料は飲むか？', placeholder: 'アルコールは飲みません。' },
    { key: 'アルコールの種類と摂取量は？', label: 'アルコールの種類と摂取量は？', placeholder: 'アルコールは飲みません。' },
    { key: 'アルコール摂取した後，歯磨きはできるか？', label: 'アルコール摂取した後，歯磨きはできるか？', placeholder: 'アルコールは飲みません。' },
    { key: '日常でよく飲む飲み物は？', label: '日常でよく飲む飲み物は？', placeholder: 'コーヒーとお茶を飲みます。', disabled: true },
    { key: '日常でよく飲む飲み物は、どの程度飲むのか？', label: '日常でよく飲む飲み物は、どの程度飲むのか？', placeholder: 'コーヒーは1日2回飲みます。お茶は、頻繁に飲みます。' },
    { key: 'どのような時に飲むのか？', label: 'どのような時に飲むのか？', placeholder: 'コーヒーは朝起きた時と、お昼を食べた後に飲みます。' },
    { key: 'コーヒーや紅茶の場合は砂糖を入れるかどうか？', label: 'コーヒーや紅茶の場合は砂糖を入れるかどうか？', placeholder: 'コーヒーはブラック、お茶は無糖で飲みます。' },
    { key: '運動習慣はありますか？', label: '運動習慣はありますか？', placeholder: 'はい、あります。' },
    { key: 'どのような運動をどの程度か？', label: 'どのような運動をどの程度か？', placeholder: 'ランニングを1週間に2回します。' },
    { key: '仕事で体を動かすことは多いか？', label: '仕事で体を動かすことは多いか？', placeholder: 'デスクワークなので、体は動かさないです。' },
    { key: '平均的な時間は？', label: '平均的な時間は？', placeholder: '毎日5時間程度です。' },
    { key: '就寝時間や起床時間は？', label: '就寝時間や起床時間は？', placeholder: '夜は1時に寝て、朝は6時に起きます。' },
    { key: '熟睡できているか？', label: '熟睡できているか？', placeholder: '寝る前にお酒を飲むことが多く、熟睡できていないかもしれません。' },
    { key: '生活の中でストレスは感じるか？', label: '生活の中でストレスは感じるか？', placeholder: '感じます。' },
    { key: 'どの程度感じているか', label: 'どの程度感じているか', placeholder: '仕事の対人関係でストレスを常に感じます。' },
    { key: 'ストレスの解消法はなにか？', label: 'ストレスの解消法はなにか？', placeholder: '特にありません。' },
  ],
  'smoking': [
    { key: '喫煙しますか？', label: '喫煙しますか？', placeholder: 'タバコは吸いません。', disabled: true },
    { key: '1日の喫煙本数', label: '1日の喫煙本数', placeholder: 'タバコは吸いません。' },
    { key: 'いつから吸っているのか', label: 'いつから吸っているのか', placeholder: 'タバコは吸いません。' },
    { key: '禁煙したいと思うか', label: '禁煙したいと思うか', placeholder: 'タバコは吸いません。' },
    { key: '禁煙に踏み込めない理由があるか', label: '禁煙に踏み込めない理由があるか', placeholder: 'タバコは吸いません。' },
    { key: '今まで禁煙経験はあるか', label: '今まで禁煙経験はあるか', placeholder: '以前は、タバコを吸っていました。' },
    { key: '禁煙が再開してしまったのはなぜか', label: '禁煙が再開してしまったのはなぜか', placeholder: '現在はタバコを吸いません。' },
    { key: '周りの人は喫煙に対してどのように言っているか', label: '周りの人は喫煙に対してどのように言っているか', placeholder: '現在はタバコを吸いません。' },
    { key: 'タバコは何年前で，何年間吸っていたかいつか', label: 'タバコは何年前で，何年間吸っていたかいつか', placeholder: '10年前まで10年間、吸っていました。' },
    { key: '以前、喫煙していた時の1日の吸う本数は？', label: '以前、喫煙していた時の1日の吸う本数は？', placeholder: '1日10本程度吸っていました。' },
    { key: 'タバコを再び吸いたいと思うか？', label: 'タバコを再び吸いたいと思うか？', placeholder: '健康のために、今後は吸いたいと思いません。' },
    { key: '家族に喫煙者がいるか？', label: '家族に喫煙者がいるか？', placeholder: 'います。' },
    { key: '家族に喫煙者がいることをどう感じるか？', label: '家族に喫煙者がいることをどう感じるか？', placeholder: '健康に良くないので止めた方が良いと感じます。' },
    { key: '受動喫煙について知っているか？', label: '受動喫煙について知っているか？', placeholder: '知っています。' },
  ],
  'oral-hygiene': [
    { key: '1日の歯磨きの回数は？', label: '1日の歯磨きの回数は？', placeholder: '1日2回磨いています。' },
    { key: 'いつ歯磨きしますか？', label: 'いつ歯磨きしますか？', placeholder: '朝食後と夕食後です。', disabled: true },
    { key: 'なぜ、1日3回歯磨きができないのか？', label: 'なぜ、1日3回歯磨きができないのか？', placeholder: '昼食後は仕事が忙しくて、磨く時間がありません。' },
    { key: '何分くらい歯磨きをしているか', label: '何分くらい歯磨きをしているか', placeholder: '1回につき5分程度磨いています。', disabled: true },
    { key: 'どのようにして歯磨きをしているか', label: 'どのようにして歯磨きをしているか', placeholder: '歯ブラシで、軽く磨く程度です。' },
    { key: '使用している歯ブラシの商品名', label: '使用している歯ブラシの商品名', placeholder: 'システマを使ってます。' },
    { key: 'なぜその歯ブラシを使用しているのか', label: 'なぜその歯ブラシを使用しているのか', placeholder: 'ドラッグストアで勧められた為です。' },
    { key: '歯ブラシはどの様な形状か？', label: '歯ブラシはどの様な形状か？', placeholder: 'ごく一般的な歯ブラシです。' },
    { key: '歯ブラシを交換する頻度は？', label: '歯ブラシを交換する頻度は？', placeholder: '1ｹ月に1回程度です。' },
    { key: '歯ブラシの交換の目安はあるか', label: '歯ブラシの交換の目安はあるか', placeholder: '毛先が開いてきたら交換しています。' },
    { key: '家では歯ブラシの保管方法はどうしているか？', label: '家では歯ブラシの保管方法はどうしているか？', placeholder: '歯ブラシ立てに立てています。' },
    { key: '会社では歯ブラシの保管方法はどうしているか？', label: '会社では歯ブラシの保管方法はどうしているか？', placeholder: '会社の机の中に入れています。' },
    { key: '歯磨き粉を使用しているか？', label: '歯磨き粉を使用しているか？', placeholder: 'はい' },
    { key: '使用している歯磨き粉の商品名は？', label: '使用している歯磨き粉の商品名は？', placeholder: 'クリアクリーン' },
    { key: 'その歯磨き粉を選んだ理由は？', label: 'その歯磨き粉を選んだ理由は？', placeholder: 'TVのCMで見たから。' },
    { key: 'その歯磨き粉は、どのような効果を謳っているのかわかるか？', label: 'その歯磨き粉は、どのような効果を謳っているのかわかるか？', placeholder: '歯垢除去力が高い。' },
    { key: '歯磨き粉の歯ブラシにつける量は？', label: '歯磨き粉の歯ブラシにつける量は？', placeholder: '歯ブラシのヘッドと同じ長さ程度の量を使います。' },
    { key: '歯磨き粉を使用していない理由は？', label: '歯磨き粉を使用していない理由は？', placeholder: '歯磨き粉を使用しています。' },
    { key: '歯ブラシ以外の清掃用具は利用しているか？', label: '歯ブラシ以外の清掃用具は利用しているか？', placeholder: 'はい、利用しています。', disabled: true },
    { key: '歯ブラシ以外で使用している清掃用具の製品と頻度は？', label: '歯ブラシ以外で使用している清掃用具の製品と頻度は？', placeholder: '歯間ブラシを、夕食後に利用しています。', disabled: true },
    { key: '使用している製品の使用法を指導されたことがあるか', label: '使用している製品の使用法を指導されたことがあるか', placeholder: 'はい、あります。' },
    { key: '歯ブラシ以外の清掃用具を使わなくなった理由は？', label: '歯ブラシ以外の清掃用具を使わなくなった理由は？', placeholder: '歯間ブラシを、現在も利用しています。' },
    { key: '歯ブラシ以外で清掃用具を使ってみたいと思うか？', label: '歯ブラシ以外で清掃用具を使ってみたいと思うか？', placeholder: 'はい、利用しています。' },
    { key: '歯ブラシ以外の清掃用具は何に興味があるか？', label: '歯ブラシ以外の清掃用具は何に興味があるか？', placeholder: 'つまようじ歯ブラシに興味があります。' },
    { key: 'マウスウォッシュは使用しているか？', label: 'マウスウォッシュは使用しているか？', placeholder: 'はい' },
    { key: 'マウスウォッシュは何をいつ使用しているか？', label: 'マウスウォッシュは何をいつ使用しているか？', placeholder: 'モンダミンを忙しくて歯磨きができない時に利用しています。' },
    { key: 'マウスウォッシュ使用の理由は？', label: 'マウスウォッシュ使用の理由は？', placeholder: '歯磨き代わりに利用しています。' },
    { key: 'マウスウォッシュは以前に使用したことがあるか？', label: 'マウスウォッシュは以前に使用したことがあるか？', placeholder: 'はい、以前から使用しています。' },
    { key: 'マウスウォッシュを使用しなくなった理由は？', label: 'マウスウォッシュを使用しなくなった理由は？', placeholder: '現在もモンダミンを使用しています。' },
  ],
  'systemic-disease': [
    { key: '全身疾患は持っているか？', label: '全身疾患は持っているか？', placeholder: 'はい、持っています。', disabled: true },
    { key: 'どの様な全身疾患ですか？', label: 'どの様な全身疾患ですか？', placeholder: '糖尿病です。', disabled: true },
    { key: '現在、糖尿病ですね？', label: '現在、糖尿病ですね？', placeholder: 'はい、糖尿病です。', disabled: true },
    { key: 'ヘモグロビンのHbA1c値はいくつか？', label: 'ヘモグロビンのHbA1c値はいくつか？', placeholder: '6％です。' },
    { key: '糖尿病の治療の為に、通院はしているか？', label: '糖尿病の治療の為に、通院はしているか？', placeholder: '通院しています。' },
    { key: '糖尿病の治療の為の通院先の医院名は？', label: '糖尿病の治療の為の通院先の医院名は？', placeholder: '青山クリニックです。' },
    { key: 'どのような治療がされているかまたは注意を受けているか（運動療法・食事療法の内容など）', label: 'どのような治療がされているかまたは注意を受けているか（運動療法・食事療法の内容など）', placeholder: '糖質を控える食事制限をしています。' },
    { key: '糖尿病の治療の為に、通院をしていない理由は？', label: '糖尿病の治療の為に、通院をしていない理由は？', placeholder: '現在、通院しています。' },
    { key: '糖尿病の治療の為に、通院をしていましたか、それとも元々通院していないのか？', label: '糖尿病の治療の為に、通院をしていましたか、それとも元々通院していないのか？', placeholder: '現在、通院しています。' },
    { key: '現在、高血圧ですね？', label: '現在、高血圧ですね？', placeholder: 'いいえ、高血圧ではありません。', disabled: true },
    { key: '収縮期血圧・拡張期血圧の値は？', label: '収縮期血圧・拡張期血圧の値は？', placeholder: 'わかりません。' },
    { key: '高血圧の治療の為に、通院しているか？', label: '高血圧の治療の為に、通院しているか？', placeholder: '高血圧ではないので通院していません。' },
    { key: '高血圧の治療の為の通院先の医院名は？', label: '高血圧の治療の為の通院先の医院名は？', placeholder: '高血圧ではないので通院していません。' },
    { key: '高血圧の治療の為に薬は飲んでいる？', label: '高血圧の治療の為に薬は飲んでいる？', placeholder: '高血圧ではないので通院していません。' },
    { key: '高血圧の治療の為に飲んでいる薬の名前は？', label: '高血圧の治療の為に飲んでいる薬の名前は？', placeholder: '高血圧ではないので通院していません。' },
    { key: '医師から言われていることがあるか（塩分摂取制限など）', label: '医師から言われていることがあるか（塩分摂取制限など）', placeholder: '高血圧ではないので特に言われていません。' },
    { key: '高血圧の治療の為に、通院していない理由は？', label: '高血圧の治療の為に、通院していない理由は？', placeholder: '高血圧ではないので通院していません。' },
    { key: '通院をしていましたか、それとも元々通院していないのか？', label: '通院をしていましたか、それとも元々通院していないのか？', placeholder: '高血圧ではないので通院していません。' },
    { key: '高血圧の治療の為に、通院をしていましたか、それとも元々通院していないのか？', label: '高血圧の治療の為に、通院をしていましたか、それとも元々通院していないのか？', placeholder: '高血圧ではないので通院していません。' },
    { key: '高血圧の治療の為に、通院をしていない理由は？', label: '高血圧の治療の為に、通院をしていない理由は？', placeholder: '高血圧ではないので通院していません。' },
    { key: '健康診断は受けているか？', label: '健康診断は受けているか？', placeholder: '受けています。' },
    { key: '健康診断の検査結果で異常値があったか？', label: '健康診断の検査結果で異常値があったか？', placeholder: 'ヘモグロビンの数値が高かったです。' },
    { key: '健康診断を受けていない理由は？', label: '健康診断を受けていない理由は？', placeholder: '健康診断は受けています。' },
    { key: 'アレルギーを持っていますか？', label: 'アレルギーを持っていますか？', placeholder: '花粉症のアレルギーです。', disabled: true },
    { key: '花粉症の症状が出る季節は？', label: '花粉症の症状が出る季節は？', placeholder: '春です。' },
    { key: 'アレルギーで飲んでいる薬は？', label: 'アレルギーで飲んでいる薬は？', placeholder: 'アレジオを飲んでいます。' },
    { key: '常用している薬はありますか？', label: '常用している薬はありますか？', placeholder: 'インスリンを常用しています。', disabled: true },
    { key: '輸血経験や手術の経験は？', label: '輸血経験や手術の経験は？', placeholder: '特にないです。' },
    { key: '血液検査での異常がありましたか？', label: '血液検査での異常がありましたか？', placeholder: '血糖値が高いです。' },
  ],
};

function InterviewModePill({ isIntake }: { isIntake: boolean }) {
  const t = isIntake
    ? { bg: '#D1FAE5', color: '#047857', label: '初診時医療面接' }
    : { bg: '#CFFAFE', color: '#0E7490', label: '医療面接' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 96,
        padding: '7px 10px',
        borderRadius: 8,
        background: t.bg,
        color: t.color,
        border: `1px solid ${t.color}`,
        fontSize: 13,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      {t.label}
    </span>
  );
}

function formatAdminDate(value: unknown) {
  if (!value) return '-';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function normalizeIdList(value: unknown): number[] {
  if (Array.isArray(value)) {
    return value.map((id) => Number(id)).filter((id) => Number.isFinite(id));
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return normalizeIdList(parsed);
    } catch {
      return [];
    }
  }
  return [];
}

function formatIdList(value: unknown) {
  const ids = normalizeIdList(value);
  return ids.length > 0 ? ids.map((id) => `#${id}`).join(', ') : '-';
}

export default function Conditions() {
  const [filterGender, setFilterGender] = useState<string>('all');
  const [filterAgeRange, setFilterAgeRange] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState<any>(null);
  const [formData, setFormData] = useState({ condition_data: '{}' });
  const [useFormMode, setUseFormMode] = useState(true); // ????????JSON???????????????
  const [useQuestionnaireFormMode, setUseQuestionnaireFormMode] = useState(false); // ???????????????????
  const [conditionFormData, setConditionFormData] = useState<ConditionFormData>({}); // ????????????
  const [questionnaireFormData, setQuestionnaireFormData] = useState<QuestionnaireFormData>({}); // ?????????????????
  const [selectedGender, setSelectedGender] = useState<string>(''); // ??????????????
  const [selectedAgeRange, setSelectedAgeRange] = useState<string>(''); // ??????????????
  
  // ????????
  const [editUseFormMode, setEditUseFormMode] = useState(true); // ????????????JSON????????
  const [editUseQuestionnaireFormMode, setEditUseQuestionnaireFormMode] = useState(false); // ????????????????
  const [editConditionFormData, setEditConditionFormData] = useState<ConditionFormData>({}); // ??????????
  const [editQuestionnaireFormData, setEditQuestionnaireFormData] = useState<QuestionnaireFormData>({}); // ???????????????
  const [editSelectedGender, setEditSelectedGender] = useState<string>(''); // ???????????
  const [editSelectedAgeRange, setEditSelectedAgeRange] = useState<string>(''); // ???????????
  const [editIsIntake, setEditIsIntake] = useState<boolean>(false); // 初診時医療面接モード（trueなら紐づく課題文は全て初診時医療面接扱い）
  const [editUseCsvMode, setEditUseCsvMode] = useState(false); // ????CSV???
  // CSV モード編集時のセル値ストア。キーは `${rowIdx}::${header}`。
  const [editGridCells, setEditGridCells] = useState<Record<string, string>>({});
  const [isCsvUploadDialogOpen, setIsCsvUploadDialogOpen] = useState(false);
  const [csvUploadGender, setCsvUploadGender] = useState<string>('');
  const [csvUploadAgeRange, setCsvUploadAgeRange] = useState<string>('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  
  // ??????????????????
  const [dynamicFormTabs, setDynamicFormTabs] = useState<Record<string, ConditionFormItem[]>>(conditionFormTabs);
  
  // ????????????????
  const [editDynamicFormTabs, setEditDynamicFormTabs] = useState<Record<string, ConditionFormItem[]>>(conditionFormTabs);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location, setLocation] = useLocation();
  const processedIdRef = useRef<string | null>(null);

  // ?????JSON????????
  const tabKeyToJsonKey: Record<string, string> = {
    'dental-history': 'dental_history',
    'lifestyle': 'lifestyle',
    'smoking': 'smoking',
    'oral-hygiene': 'oral_hygiene',
    'systemic-disease': 'systemic_disease',
  };

  // ????????JSON???
  const convertFormDataToJson = (): any => {
    if (useQuestionnaireFormMode) {
      return convertQuestionnaireFormDataToJson();
    }
    
    const result: any = {
      dental_history: {},
      lifestyle: {},
      smoking: {},
      oral_hygiene: {},
      systemic_disease: {},
    };

    // ????????????????????????
    Object.keys(dynamicFormTabs).forEach((tabKey) => {
      const jsonKey = tabKeyToJsonKey[tabKey] || tabKey;
      const tabItems = dynamicFormTabs[tabKey];
      tabItems.forEach((item) => {
        const value = conditionFormData[item.key] || '';
        if (value.trim() !== '') {
          // ???Q?????????????????????????????
          // ?????????????????
          const questionKey = item.label.trim() || item.key;
          result[jsonKey][questionKey] = value;
        }
      });
    });

    return result;
  };

  // ??????????????JSON???
  const convertQuestionnaireFormDataToJson = (qDataParam: QuestionnaireFormData = questionnaireFormData): any => {
    const result: any = {
      dental_history: {},
      lifestyle: {},
      smoking: {},
      oral_hygiene: {},
      systemic_disease: {},
    };

    const qData = qDataParam;

    // [?] ??????????
    const symptoms = (qData['complaint1_symptoms'] as string[]) || [];
    const otherSymptoms = (qData['complaint1_other'] as string) || '';
    if (symptoms.length > 0 || otherSymptoms) {
      const symptomTexts: string[] = [];
      if (symptoms.includes('????')) symptomTexts.push('????');
      if (symptoms.includes('?????')) symptomTexts.push('?????');
      if (symptoms.includes('???????')) symptomTexts.push('???????');
      if (symptoms.includes('???????')) symptomTexts.push('???????');
      if (symptoms.includes('???????')) symptomTexts.push('???????');
      if (symptoms.includes('??????')) symptomTexts.push('??????');
      if (symptoms.includes('??????????')) symptomTexts.push('??????????');
      
      result.dental_history['???????????????'] = symptoms.length > 0 ? '????????' : '';
      result.dental_history['????????????????????????????????'] = 
        [...symptomTexts, otherSymptoms].filter(Boolean).join('?') || '';
    }

    // [?] ?????????????????
    const diseases = (qData['complaint2_diseases'] as string[]) || [];
    if (diseases.includes('none')) {
      result.systemic_disease['????????????'] = '????????????';
    } else if (diseases.length > 0) {
      result.systemic_disease['????????????'] = '??????????';
      const diseaseTypes: string[] = [];
      if (diseases.includes('hypertension')) {
        diseaseTypes.push('???');
        result.systemic_disease['??????????'] = '?????????';
      }
      if (diseases.includes('heart_disease')) {
        diseaseTypes.push('???');
      }
      if (diseases.includes('diabetes')) {
        diseaseTypes.push('???');
        result.systemic_disease['??????????'] = '?????????';
      }
      if (diseases.includes('stroke')) diseaseTypes.push('???');
      if (diseases.includes('asthma')) diseaseTypes.push('????');
      if (diseases.includes('liver_disease')) diseaseTypes.push('???');
      if (diseases.includes('kidney_disease')) diseaseTypes.push('???');
      if (diseases.includes('epilepsy')) diseaseTypes.push('????');
      if (diseases.includes('allergy')) {
        diseaseTypes.push('?????');
        result.systemic_disease['??????????????'] = '????????';
      }
      if (diseases.includes('thyroid')) diseaseTypes.push('??????');
      if (diseases.includes('cancer')) diseaseTypes.push('??');
      if (diseases.includes('dementia')) diseaseTypes.push('???');
      if (diseases.includes('hiv')) diseaseTypes.push('HIV');
      
      const otherDisease = (qData['complaint2_other'] as string) || '';
      if (otherDisease) diseaseTypes.push(otherDisease);
      
      result.systemic_disease['????????????'] = diseaseTypes.join('?') || '';
    }

    // [?] ???????????????
    const hasMedication = qData['complaint3_has_medication'] as string;
    if (hasMedication === 'yes') {
      result.systemic_disease['??????????????'] = '????????';
      const medicationName = (qData['complaint3_medication_name'] as string) || '';
      if (medicationName) {
        result.systemic_disease['??????????????'] = `???${medicationName}`;
      }
    } else {
      result.systemic_disease['??????????????'] = '????????';
    }

    // [?] ????????????????
    const badFeeling = qData['complaint4_bad_feeling'] as string;
    if (badFeeling === 'yes') {
      result.dental_history['?????????????????????????'] = '????????';
    } else {
      result.dental_history['?????????????????????????'] = '??????????';
    }

    const allergy = qData['complaint4_allergy'] as string;
    if (allergy === 'yes' || allergy === 'suspected') {
      result.systemic_disease['??????????????'] = allergy === 'yes' ? '????????' : '????????';
    } else {
      result.systemic_disease['??????????????'] = '????????';
    }

    // [?] ????????
    const brushingTimes = (qData['complaint5_brushing_times'] as string[]) || [];
    const brushingTimeLabels: string[] = [];
    if (brushingTimes.includes('morning')) brushingTimeLabels.push('???');
    if (brushingTimes.includes('after_breakfast')) brushingTimeLabels.push('???');
    if (brushingTimes.includes('after_lunch')) brushingTimeLabels.push('???');
    if (brushingTimes.includes('after_dinner')) brushingTimeLabels.push('???');
    if (brushingTimes.includes('before_bed')) brushingTimeLabels.push('???');
    result.oral_hygiene['??????????'] = brushingTimeLabels.join('?') || '';

    const brushingDuration = (qData['complaint5_brushing_duration'] as string) || '';
    if (brushingDuration) {
      result.oral_hygiene['??????????????'] = `${brushingDuration}??????????`;
    }

    const brushingTools = (qData['complaint5_brushing_tools'] as string[]) || [];
    const toolLabels: string[] = [];
    if (brushingTools.includes('toothbrush')) toolLabels.push('????');
    if (brushingTools.includes('floss')) toolLabels.push('???');
    if (brushingTools.includes('interdental')) toolLabels.push('?????');
    const otherTool = (qData['complaint5_brushing_tools_other'] as string) || '';
    if (otherTool) toolLabels.push(otherTool);
    result.oral_hygiene['????????????????????'] = toolLabels.length > 0 ? '???????????' : '';
    if (toolLabels.length > 0) {
      result.oral_hygiene['?????????????????????????'] = toolLabels.join('?') + '?????????';
    }

    const smoking = qData['complaint5_smoking'] as string;
    if (smoking === 'none') {
      result.smoking['???????'] = '??????????';
    } else if (smoking === 'yes') {
      result.smoking['???????'] = '????????';
    } else if (smoking === 'past') {
      result.smoking['???????'] = '????????????????';
    }

    const sleepHours = (qData['complaint5_sleep_hours'] as string) || '';
    if (sleepHours) {
      result.lifestyle['???????????'] = `?????1???${sleepHours}???????`;
    }

    const hasDrinks = qData['complaint5_has_drinks'] as string;
    if (hasDrinks === 'yes') {
      const drinksName = (qData['complaint5_drinks_name'] as string) || '';
      result.lifestyle['????????????'] = drinksName || '?????????????';
    } else {
      result.lifestyle['????????????'] = '????????';
    }

    const snacking = qData['complaint5_snacking'] as string;
    if (snacking === 'none') {
      result.lifestyle['???????'] = '???????';
    } else if (snacking === 'regular') {
      result.lifestyle['???????'] = '??????????';
    } else if (snacking === 'irregular') {
      result.lifestyle['???????'] = '?????????';
    }

    // [?] ??????
    const treatments = (qData['complaint6_treatments'] as string[]) || [];
    if (treatments.includes('??????????????????')) {
      result.dental_history['???????????'] = '??????????????????';
    }

    return result;
  };

  // JSON??????????????????????
  const convertJsonToFormData = (jsonData: any): { formData: ConditionFormData; gender: string; ageRange: string; formTabs: Record<string, ConditionFormItem[]> } => {
    const formData: ConditionFormData = {};
    const formTabs: Record<string, ConditionFormItem[]> = {
      'dental-history': [],
      'lifestyle': [],
      'smoking': [],
      'oral-hygiene': [],
      'systemic-disease': [],
    };

    // ??????????????????
    const tabMapping: Record<string, string> = {
      'dental_history': 'dental-history',
      'lifestyle': 'lifestyle',
      'smoking': 'smoking',
      'oral_hygiene': 'oral-hygiene',
      'systemic_disease': 'systemic-disease',
    };

    Object.keys(tabMapping).forEach((jsonKey) => {
      const tabKey = tabMapping[jsonKey];
      if (jsonData[jsonKey]) {
        Object.keys(jsonData[jsonKey]).forEach((questionKey) => {
          // JSON?????????????label?????
          // item.key????ID?????????????????????????
          const existingItem = conditionFormTabs[tabKey]?.find((item) => item.key === questionKey || item.label === questionKey);
          let itemKey: string;
          if (existingItem) {
            itemKey = existingItem.key;
            // ?????????????????label????
            if (!formTabs[tabKey].find((item) => item.key === itemKey)) {
              formTabs[tabKey].push({
                ...existingItem,
                label: questionKey, // JSON?????????label?????
              });
            }
          } else {
            // ??????????questionKey?key?????
            itemKey = questionKey;
            formTabs[tabKey].push({
              key: itemKey,
              label: questionKey, // JSON?????????label?????
              placeholder: '???????????',
            });
          }
          formData[itemKey] = jsonData[jsonKey][questionKey];
        });
      }
    });

    // ???conditionFormTabs????????????????????
    Object.keys(conditionFormTabs).forEach((tabKey) => {
      conditionFormTabs[tabKey].forEach((item) => {
        if (!formTabs[tabKey].find((i) => i.key === item.key)) {
          formTabs[tabKey].push(item);
        }
      });
    });

    return { formData, gender: '', ageRange: '', formTabs };
  };

  // JSON????????????????????????????
  const convertJsonToQuestionnaireFormData = (jsonData: any): QuestionnaireFormData => {
    const qData: QuestionnaireFormData = {};

    // [?] ??????????
    const concernLocation = jsonData.dental_history?.['????????????????????????????????'] || '';
    const symptoms: string[] = [];
    if (concernLocation.includes('????')) symptoms.push('????');
    if (concernLocation.includes('??')) symptoms.push('?????');
    if (concernLocation.includes('???') && (concernLocation.includes('??') || concernLocation.includes('??'))) symptoms.push('???????');
    if (concernLocation.includes('??')) symptoms.push('???????');
    if (concernLocation.includes('???') && concernLocation.includes('??')) symptoms.push('???????');
    if (concernLocation.includes('??') && concernLocation.includes('??')) symptoms.push('??????');
    if (concernLocation.includes('????') || concernLocation.includes('???')) symptoms.push('??????????');
    qData['complaint1_symptoms'] = symptoms;
    const otherSymptoms = concernLocation.split(/[、,]/).filter((s: string) => !symptoms.some((sym: string) => s.includes(sym))).join('、');
    if (otherSymptoms) qData['complaint1_other'] = otherSymptoms;

    // [?] ?????????????????
    const hasSystemicDisease = jsonData.systemic_disease?.['????????????'] || '';
    const diseases: string[] = [];
    if (hasSystemicDisease.includes('???') || hasSystemicDisease.includes('??')) {
      diseases.push('none');
    } else {
      const diseaseType = jsonData.systemic_disease?.['????????????'] || '';
      if (diseaseType.includes('???')) diseases.push('hypertension');
      if (diseaseType.includes('??')) diseases.push('heart_disease');
      if (diseaseType.includes('???')) diseases.push('diabetes');
      if (diseaseType.includes('???')) diseases.push('stroke');
      if (diseaseType.includes('????')) diseases.push('asthma');
      if (diseaseType.includes('??')) diseases.push('liver_disease');
      if (diseaseType.includes('??')) diseases.push('kidney_disease');
      if (diseaseType.includes('????')) diseases.push('epilepsy');
      if (diseaseType.includes('?????')) diseases.push('allergy');
      if (diseaseType.includes('???')) diseases.push('thyroid');
      if (diseaseType.includes('??')) diseases.push('cancer');
      if (diseaseType.includes('???')) diseases.push('dementia');
      if (diseaseType.includes('HIV')) diseases.push('hiv');
    }
    qData['complaint2_diseases'] = diseases;

    // [?] ???????????????
    const hasMedication = jsonData.systemic_disease?.['??????????????'] || '';
    if (hasMedication.includes('???') || hasMedication.includes('??')) {
      qData['complaint3_has_medication'] = 'none';
    } else {
      qData['complaint3_has_medication'] = 'yes';
      const medicationName = hasMedication.replace(/なし[、,]?/, '').trim();
      if (medicationName) qData['complaint3_medication_name'] = medicationName;
    }

    // [?] ????????????????
    const badFeeling = jsonData.dental_history?.['?????????????????????????'] || '';
    qData['complaint4_bad_feeling'] = badFeeling.includes('??') ? 'yes' : 'no';

    const allergy = jsonData.systemic_disease?.['??????????????'] || '';
    if (allergy.includes('??')) {
      qData['complaint4_allergy'] = 'yes';
    } else if (allergy.includes('???') || allergy.includes('??')) {
      qData['complaint4_allergy'] = 'suspected';
    } else {
      qData['complaint4_allergy'] = 'no';
    }
    qData['complaint4_pregnancy'] = 'no'; // ?????

    // [?] ????????
    const brushingTimes = jsonData.oral_hygiene?.['??????????'] || '';
    const brushingTimeKeys: string[] = [];
    if (brushingTimes.includes('???')) brushingTimeKeys.push('morning');
    if (brushingTimes.includes('???')) brushingTimeKeys.push('after_breakfast');
    if (brushingTimes.includes('???')) brushingTimeKeys.push('after_lunch');
    if (brushingTimes.includes('???')) brushingTimeKeys.push('after_dinner');
    if (brushingTimes.includes('???')) brushingTimeKeys.push('before_bed');
    qData['complaint5_brushing_times'] = brushingTimeKeys;

    const brushingDuration = jsonData.oral_hygiene?.['??????????????'] || '';
    const durationMatch = brushingDuration.match(/(\d+)/);
    if (durationMatch) qData['complaint5_brushing_duration'] = durationMatch[1];

    const brushingTools = jsonData.oral_hygiene?.['?????????????????????????'] || '';
    const toolKeys: string[] = [];
    if (brushingTools.includes('???')) toolKeys.push('floss');
    if (brushingTools.includes('?????')) toolKeys.push('interdental');
    qData['complaint5_brushing_tools'] = toolKeys;

    const smoking = jsonData.smoking?.['???????'] || '';
    if (smoking.includes('???') || smoking.includes('??')) {
      qData['complaint5_smoking'] = 'none';
    } else if (smoking.includes('??') || smoking.includes('??')) {
      qData['complaint5_smoking'] = 'past';
    } else {
      qData['complaint5_smoking'] = 'yes';
    }

    const sleepTime = jsonData.lifestyle?.['???????????'] || '';
    const sleepMatch = sleepTime.match(/(\d+)\s*時間/);
    if (sleepMatch) qData['complaint5_sleep_hours'] = sleepMatch[1];

    const drinks = jsonData.lifestyle?.['????????????'] || '';
    if (drinks.includes('???') || drinks.includes('??')) {
      qData['complaint5_has_drinks'] = 'none';
    } else {
      qData['complaint5_has_drinks'] = 'yes';
      qData['complaint5_drinks_name'] = drinks;
    }

    const snacking = jsonData.lifestyle?.['???????'] || '';
    if (snacking.includes('???')) {
      qData['complaint5_snacking'] = 'none';
    } else if (snacking.includes('??')) {
      qData['complaint5_snacking'] = 'regular';
    } else if (snacking.includes('???')) {
      qData['complaint5_snacking'] = 'irregular';
    }

    // [?] ??????
    const wantsGuidance = jsonData.dental_history?.['???????????'] || '';
    const treatments: string[] = [];
    if (wantsGuidance.includes('??') || wantsGuidance.includes('??')) {
      treatments.push('??????????????????');
    }
    qData['complaint6_treatments'] = treatments;

    return qData;
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'conditions', filterGender, filterAgeRange, page],
    queryFn: () => getConditions({ 
      gender: filterGender !== 'all' ? filterGender : undefined, 
      age_range: filterAgeRange !== 'all' ? filterAgeRange : undefined, 
      page, 
      limit: 20 
    }),
  });

  // URL???????ID????????????????
  useEffect(() => {
    const params = new URLSearchParams(location.split('?')[1]);
    const idParam = params.get('id');
    
    // ???????ID????????
    if (!idParam || idParam === processedIdRef.current) {
      return;
    }
    
    if (!isLoading) {
      const conditionId = parseInt(idParam, 10);
      processedIdRef.current = idParam;
      
      // ??????????????
      const condition = data?.conditions?.find((c: any) => c.id === conditionId);
      
      if (condition) {
        handleEdit(condition);
        // URL??????????
        window.history.replaceState({}, '', '/admin/conditions');
        processedIdRef.current = null; // ?????????
      } else if (data && !isLoading) {
        // ?????????????????API??????
        getCondition(conditionId)
          .then((response) => {
            if (response.condition) {
              handleEdit(response.condition);
              // URL??????????
              window.history.replaceState({}, '', '/admin/conditions');
            }
            processedIdRef.current = null; // ?????????
          })
          .catch((error) => {
            console.error('??????????????:', error);
            processedIdRef.current = null; // ?????????
          });
      }
    }
  }, [location, data, isLoading]);

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateCondition(id, data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'conditions'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'all-condition-instructions'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'all-condition-questionnaires'] });
      // 個別の課題文・問診票ビューもinvalidate（編集ダイアログのレビュートグルが古い状態のままにならないように）
      queryClient.invalidateQueries({ queryKey: ['admin', 'condition-instruction'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'condition-questionnaire'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'condition-instructions'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'condition-questionnaires'] });
      setIsEditDialogOpen(false);
      setSelectedCondition(null);
      setEditConditionFormData({});
      setEditQuestionnaireFormData({});
      setEditSelectedGender('');
      setEditSelectedAgeRange('');
      setEditUseQuestionnaireFormMode(false);
      setEditUseCsvMode(false);
      const reverted = (response?.revertedInstructions ?? 0) + (response?.revertedQuestionnaires ?? 0);
      if (response?.contentChanged && reverted > 0) {
        toast({
          title: '編集成功',
          description: `課題文と問診票がレビュー中に戻されました。レビューしなおしてください。（課題文 ${response.revertedInstructions}件 / 問診票 ${response.revertedQuestionnaires}件）`,
        });
      } else {
        toast({
          title: '成功',
          description: '更新しました',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || '更新に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCondition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'conditions'] });
      setIsDeleteDialogOpen(false);
      setSelectedCondition(null);
      toast({
        title: '成功',
        description: '健康心理条件を削除しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || '削除に失敗しました',
        variant: 'destructive',
      });
    },
  });


  const handleEdit = (condition: any) => {
    setSelectedCondition(condition);
    const conditionData = condition.condition_data || {};
    
    // CSV / categoryTableV2 / V3 categoryDefinitions は表形式で扱う
    const hasCsvData = hasConditionCategoryGrid(conditionData);
    
    if (hasCsvData) {
      setEditUseCsvMode(true);
      setEditUseQuestionnaireFormMode(false);
      // 既存 csvData.rows / categoryTableV2 をテーブル表示用 grid に変換して、
      // 編集用ストアを初期化する。キーは `${rowIdx}::${header}`。
      const initialGrid = conditionDataToCellGrid(conditionData);
      const cells: Record<string, string> = {};
      if (initialGrid) {
        initialGrid.rows.forEach((row, rowIdx) => {
          for (const header of initialGrid.headers) {
            const cell = row.cells.find((c: any) => c.header === header);
            cells[`${rowIdx}::${header}`] = cell?.value ?? '';
          }
        });
      }
      setEditGridCells(cells);
    } else {
      setEditGridCells({});
      setEditUseCsvMode(false);
      setEditUseQuestionnaireFormMode(true);
      const { formData, formTabs } = convertJsonToFormData(conditionData);
      setEditConditionFormData(formData);
      setEditDynamicFormTabs(formTabs);
      const questionnaireFormData = convertJsonToQuestionnaireFormData(conditionData);
      setEditQuestionnaireFormData(questionnaireFormData);
    }
    
    setEditSelectedGender(condition.gender || '');
    setEditSelectedAgeRange(condition.age_range || '');
    setEditIsIntake(!!conditionData.is_intake);

    setFormData({
      condition_data: JSON.stringify(conditionData, null, 2),
    });

    setIsEditDialogOpen(true);
  };


  // ????????????JSON???
  const convertEditFormDataToJson = (): any => {
    if (editUseQuestionnaireFormMode) {
      return convertQuestionnaireFormDataToJson(editQuestionnaireFormData);
    }
    
    const result: any = {
      dental_history: {},
      lifestyle: {},
      smoking: {},
      oral_hygiene: {},
      systemic_disease: {},
    };

    // ????????????????????????
    Object.keys(editDynamicFormTabs).forEach((tabKey) => {
      const jsonKey = tabKeyToJsonKey[tabKey] || tabKey;
      const tabItems = editDynamicFormTabs[tabKey];
      tabItems.forEach((item) => {
        const value = editConditionFormData[item.key] || '';
        if (value.trim() !== '') {
          // ???Q?????????????????????????????
          const questionKey = item.label.trim() || item.key;
          result[jsonKey][questionKey] = value;
        }
      });
    });

    return result;
  };

  const handleUpdate = () => {
    if (selectedCondition) {
      if (!editSelectedGender || !editSelectedAgeRange) {
        toast({
          title: 'エラー',
          description: '性別と年齢層を選択してください',
          variant: 'destructive',
        });
        return;
      }

      try {
        // 既存の condition_data を保持し、性別・年齢・intakeフラグを更新
        const conditionData: any = { ...(selectedCondition.condition_data || {}), is_intake: editIsIntake };

        // CSV / V3 表モードのとき: 編集された editGridCells を元データへ書き戻す。
        // 表示用ヘッダー (ID / カテゴリ / 備考 / Lv1_有無・事実 ...) と CSV ヘッダー (id / category / notes / Lv1_有無・事実 ...) のマッピング。
        if (editUseCsvMode && selectedConditionGrid) {
          if (conditionData.conditionTableVersion === 3 || Array.isArray(conditionData.categoryDefinitions)) {
            const updatedConditionData = applyCellGridEditsToV3ConditionData(conditionData, selectedConditionGrid, editGridCells);
            Object.keys(conditionData).forEach((key) => delete conditionData[key]);
            Object.assign(conditionData, updatedConditionData);
            // V3 は categoryDefinitions を正とする。過去保存で混ざった表派生データはここで落とす。
            delete conditionData.csvData;
            delete conditionData.categoryTable;
            delete conditionData.categoryTableV2;
          } else {
            const oldCsv = (conditionData.csvData ?? {}) as { headers?: string[]; rows?: Record<string, string>[]; rowCount?: number };
            const newHeaders = oldCsv.headers?.length
              ? oldCsv.headers
              : ['id', 'category', ...selectedConditionGrid.headers.filter((h) => h !== 'ID' && h !== 'カテゴリ' && h !== '備考'), 'notes'];
            const headerToCsv: Record<string, string> = { ID: 'id', カテゴリ: 'category', 備考: 'notes' };
            const newRows = selectedConditionGrid.rows.map((row: any, rowIdx: number) => {
              const r: Record<string, string> = { ...(oldCsv.rows?.[rowIdx] ?? {}) };
              for (const header of selectedConditionGrid.headers) {
                const csvHeader = headerToCsv[header] ?? header;
                r[csvHeader] = editGridCells[`${rowIdx}::${header}`] ?? '';
              }
              return r;
            });
            conditionData.csvData = {
              ...oldCsv,
              headers: newHeaders,
              rows: newRows,
              rowCount: newRows.length,
            };
            // categoryTable / categoryTableV2 はサーバで再生成されるので削除
            delete conditionData.categoryTable;
            delete conditionData.categoryTableV2;
          }
        }

        updateMutation.mutate({
          id: selectedCondition.id,
          data: {
            condition_data: conditionData,
            gender: editSelectedGender,
            age_range: editSelectedAgeRange
          }
        });
      } catch (e) {
        toast({
          title: 'エラー',
          description: '更新に失敗しました',
          variant: 'destructive',
        });
      }
    }
  };

  const canDelete = (condition: any) => {
    if (!data?.conditions) return false;
    const sameGroup = data.conditions.filter(
      (c: any) => c.gender === condition.gender && c.age_range === condition.age_range
    );
    return sameGroup.length > 1;
  };

  const handleDelete = (condition: any) => {
    setSelectedCondition(condition);
    setIsDeleteDialogOpen(true);
  };

  const handleDuplicate = async (condition: any) => {
    try {
      const detail = await getCondition(condition.id);
      const src = detail.condition;
      const created = await createCondition({
        condition_data: src.condition_data,
        gender: src.gender || '',
        age_range: src.age_range || '',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-conditions'] });
      const newCondition = created?.condition;
      if (newCondition) {
        toast({ title: '複製しました', description: `新しいID: ${newCondition.id}（編集画面を開きます）` });
        handleEdit(newCondition);
      } else {
        toast({ title: '成功', description: `ID ${condition.id} を複製しました` });
      }
    } catch (e: any) {
      toast({ title: 'エラー', description: e.message || '複製に失敗しました', variant: 'destructive' });
    }
  };

  const csvExportMutation = useMutation({
    mutationFn: () => exportConditionsToCsv({
      gender: filterGender !== 'all' ? filterGender : undefined,
      age_range: filterAgeRange !== 'all' ? filterAgeRange : undefined,
    }),
    onSuccess: () => {
      toast({
        title: '成功',
        description: 'CSVダウンロードが完了しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'CSVダウンロードに失敗しました',
        variant: 'destructive',
      });
    },
  });

  const csvImportMutation = useMutation({
    mutationFn: () => {
      if (!csvFile || !csvUploadGender || !csvUploadAgeRange) {
        throw new Error('CSVファイル、性別、年齢層を選択してください');
      }
      return importConditionsFromCsv(csvFile, csvUploadGender, csvUploadAgeRange);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'conditions'] });
      setIsCsvUploadDialogOpen(false);
      setCsvFile(null);
      setCsvUploadGender('');
      setCsvUploadAgeRange('');
      toast({
        title: '成功',
        description: `CSV: ${result.rowsProcessed || result.imported}件インポートしました${result.errors > 0 ? `（エラー: ${result.errors}件）` : ''}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'CSVインポートに失敗しました',
        variant: 'destructive',
      });
    },
  });

  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast({
          title: 'エラー',
          description: 'CSVファイルを選択してください',
          variant: 'destructive',
        });
        return;
      }
      setCsvFile(file);
    }
  };
  const confirmDelete = () => {
    if (selectedCondition) {
      deleteMutation.mutate(selectedCondition.id);
    }
  };
  const selectedConditionGrid = conditionDataToCellGrid(selectedCondition?.condition_data);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900">健康心理条件管理</h1>
            <p className="text-gray-500 mt-2 text-sm">健康心理条件の管理</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => csvExportMutation.mutate()}
              disabled={csvExportMutation.isPending}
              className="h-11 rounded-lg border-gray-200"
            >
              {csvExportMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              CSVダウンロード
            </Button>
            <Dialog open={isCsvUploadDialogOpen} onOpenChange={setIsCsvUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="h-11 rounded-lg border-gray-200">
                  <Upload className="w-4 h-4 mr-2" />
                  CSVアップロード
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>CSVアップロード</DialogTitle>
                  <DialogDescription>
                    CSVファイルをアップロードして健康心理条件を一括登録します
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="csv-gender">性別</Label>
                    <Select value={csvUploadGender} onValueChange={setCsvUploadGender}>
                      <SelectTrigger id="csv-gender">
                        <SelectValue placeholder="選択してください" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="男性">男性</SelectItem>
                        <SelectItem value="女性">女性</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="csv-age-range">年齢層</Label>
                    <Select value={csvUploadAgeRange} onValueChange={setCsvUploadAgeRange}>
                      <SelectTrigger id="csv-age-range">
                        <SelectValue placeholder="選択してください" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20-34">20-34</SelectItem>
                        <SelectItem value="35-49">35-49</SelectItem>
                        <SelectItem value="50+">50+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="csv-file">CSVファイル</Label>
                    <Input
                      id="csv-file"
                      type="file"
                      accept=".csv"
                      onChange={handleCsvFileChange}
                    />
                    {csvFile && (
                      <p className="text-sm text-gray-600 mt-2">
                        選択済み: {csvFile.name}
                      </p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsCsvUploadDialogOpen(false)}
                  >
                    キャンセル
                  </Button>
                  <Button
                    onClick={() => csvImportMutation.mutate()}
                    disabled={csvImportMutation.isPending || !csvFile || !csvUploadGender || !csvUploadAgeRange}
                  >
                    {csvImportMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        アップロード中...
                      </>
                    ) : (
                      'アップロード'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>


        {/* フィルタ */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="filter-gender" className="text-base font-bold text-gray-700">性別</Label>
              <Select
                value={filterGender}
                onValueChange={(value) => {
                  setFilterGender(value);
                  setPage(1);
                }}
              >
                <SelectTrigger id="filter-gender" className="h-11 rounded-xl border-gray-200">
                  <SelectValue placeholder="すべて" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべて</SelectItem>
                  <SelectItem value="男性">男性</SelectItem>
                  <SelectItem value="女性">女性</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="filter-age-range" className="text-base font-bold text-gray-700">年齢層</Label>
              <Select
                value={filterAgeRange}
                onValueChange={(value) => {
                  setFilterAgeRange(value);
                  setPage(1);
                }}
              >
                <SelectTrigger id="filter-age-range" className="h-11 rounded-xl border-gray-200">
                  <SelectValue placeholder="すべて" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべて</SelectItem>
                  <SelectItem value="20-34">20-34</SelectItem>
                  <SelectItem value="35-49">35-49</SelectItem>
                  <SelectItem value="50+">50+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* 条件一覧 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">健康心理条件一覧</div>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">エラーが発生しました</div>
          ) : (
            <>
                {(() => {
                  // CSV / categoryTableV2 / V3 categoryDefinitions の表データがある条件を探す
                  const tableCondition = data?.conditions?.find((c: any) => hasConditionCategoryGrid(c.condition_data));
                  const tableGrid = tableCondition ? conditionDataToCellGrid(tableCondition.condition_data) : null;
                  const hasCsvData = !!tableGrid;
                  const csvHeaders = tableGrid?.headers || [];
                  
                  return hasCsvData ? (
                    // CSVデータがある場合は新しいテーブル形式で表示
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>ID</TableHead>
                            <TableHead>性別</TableHead>
                            <TableHead>年齢層</TableHead>
                            <TableHead>モード</TableHead>
                            {csvHeaders.map((header: string, idx: number) => (
                              <TableHead key={idx}>{header}</TableHead>
                            ))}
                            <TableHead>作成日時</TableHead>
                            <TableHead>更新日時</TableHead>
                            <TableHead>課題文</TableHead>
                            <TableHead>問診票</TableHead>
                            <TableHead>操作</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.conditions.map((condition: any) => {
                            const grid = conditionDataToCellGrid(condition.condition_data);
                            const firstRow = grid?.rows?.[0];
                            return (
                              <TableRow key={condition.id}>
                                <TableCell>{condition.id}</TableCell>
                                <TableCell className="font-medium">
                                  {condition.gender || '-'}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {condition.age_range || '-'}
                                </TableCell>
                                <TableCell>
                                  <InterviewModePill isIntake={!!condition.condition_data?.is_intake} />
                                </TableCell>
                                {csvHeaders.map((header: string, headerIdx: number) => {
                                  const value = firstRow?.cells.find((cell: any) => cell.header === header)?.value;
                                  return (
                                    <TableCell key={headerIdx}>
                                      {value !== undefined && value !== null && value !== '' 
                                        ? (typeof value === 'object' ? JSON.stringify(value) : String(value))
                                        : '-'
                                      }
                                    </TableCell>
                                  );
                                })}
                                <TableCell>
                                  {formatAdminDate(condition.created)}
                                </TableCell>
                                <TableCell>
                                  {formatAdminDate(condition.updated)}
                                </TableCell>
                                <TableCell>
                                  <div
                                    className="flex items-center gap-2 whitespace-nowrap"
                                    title={`全課題文ID: ${formatIdList(condition.instruction_ids)}`}
                                  >
                                    <div className="flex flex-col">
                                      <span className="text-sm">レビュー済み {condition.approved_instructions_count ?? 0}件</span>
                                      <span className="text-xs text-gray-500 tabular-nums">
                                        {formatIdList(condition.approved_instruction_ids)}
                                      </span>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 px-2"
                                      onClick={() => setLocation(`/admin/instructions?conditionId=${condition.id}`)}
                                      title="課題文を作成/編集"
                                    >
                                      <Plus className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div
                                    className="flex items-center gap-2 whitespace-nowrap"
                                    title={`全問診票ID: ${formatIdList(condition.questionnaire_ids)}`}
                                  >
                                    <div className="flex flex-col">
                                      <span className={`text-sm ${condition.has_approved_questionnaire ? 'text-green-600' : 'text-gray-400'}`}>
                                        {condition.has_approved_questionnaire ? '✓ 承認済' : '× なし'}
                                      </span>
                                      <span className="text-xs text-gray-500 tabular-nums">
                                        {formatIdList(condition.approved_questionnaire_ids)}
                                      </span>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 px-2"
                                      onClick={() => setLocation(`/admin/questionnaires?conditionId=${condition.id}`)}
                                      title="問診票を編集"
                                    >
                                      <Edit className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEdit(condition)}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDuplicate(condition)}
                                      title="複製"
                                    >
                                      <Copy className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDelete(condition)}
                                      disabled={!canDelete(condition)}
                                      title={canDelete(condition) ? '削除' : '同じ性別×年齢の最後の1件は削除できません'}
                                    >
                                      <Trash2 className={`w-4 h-4 ${canDelete(condition) ? 'text-red-600' : 'text-gray-300'}`} />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    // CSVデータがない場合は従来の形式で表示
                    <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-gray-200 hover:bg-transparent">
                          <TableHead className="text-base font-bold text-gray-700">ID</TableHead>
                          <TableHead className="text-base font-bold text-gray-700">性別</TableHead>
                          <TableHead className="text-base font-bold text-gray-700">年齢層</TableHead>
                          <TableHead className="text-base font-bold text-gray-700 text-center">モード</TableHead>
                          <TableHead className="text-base font-bold text-gray-700 text-center">作成日時</TableHead>
                          <TableHead className="text-base font-bold text-gray-700 text-center">更新日時</TableHead>
                          <TableHead className="text-base font-bold text-gray-700 text-center">課題文</TableHead>
                          <TableHead className="text-base font-bold text-gray-700 text-center">問診票</TableHead>
                          <TableHead className="text-base font-bold text-gray-700 text-center">操作</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data?.conditions?.map((condition: any) => {
                          return (
                            <TableRow key={condition.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                              <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{condition.id}</TableCell>
                              <TableCell className="py-4 text-base font-medium text-gray-900">
                                {condition.gender || '-'}
                              </TableCell>
                              <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">
                                {condition.age_range || '-'}
                              </TableCell>
                              <TableCell className="py-4 text-center">
                                <InterviewModePill isIntake={!!condition.condition_data?.is_intake} />
                              </TableCell>
                              <TableCell className="py-4 text-center text-base font-medium text-gray-900 tabular-nums">
                                {formatAdminDate(condition.created)}
                              </TableCell>
                              <TableCell className="py-4 text-center text-base font-medium text-gray-900 tabular-nums">
                                {formatAdminDate(condition.updated)}
                              </TableCell>
                              <TableCell className="py-4">
                                <div
                                  className="flex items-center justify-center gap-2 whitespace-nowrap"
                                  title={`全課題文ID: ${formatIdList(condition.instruction_ids)}`}
                                >
                                  <div className="flex flex-col items-center">
                                    <span className="text-base font-medium text-gray-900">
                                      レビュー済み
                                      <span className="inline-block min-w-[2ch] text-right font-bold tabular-nums mx-1">
                                        {condition.approved_instructions_count ?? 0}
                                      </span>
                                      件
                                    </span>
                                    <span className="text-xs text-gray-500 tabular-nums">
                                      {formatIdList(condition.approved_instruction_ids)}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setLocation(`/admin/instructions?conditionId=${condition.id}`)}
                                    title="課題文を作成/編集"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </TableCell>
                              <TableCell className="py-4">
                                <div
                                  className="flex items-center justify-center gap-2 whitespace-nowrap"
                                  title={`全問診票ID: ${formatIdList(condition.questionnaire_ids)}`}
                                >
                                  <div className="flex flex-col items-center">
                                    <span className={`inline-flex items-center gap-1 w-20 text-base font-bold ${condition.has_approved_questionnaire ? 'text-emerald-700' : 'text-gray-500'}`}>
                                      <span className="w-4 text-center shrink-0">{condition.has_approved_questionnaire ? '✓' : '×'}</span>
                                      <span className="flex-1 text-center">{condition.has_approved_questionnaire ? '承認済' : 'なし'}</span>
                                    </span>
                                    <span className="text-xs text-gray-500 tabular-nums">
                                      {formatIdList(condition.approved_questionnaire_ids)}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setLocation(`/admin/questionnaires?conditionId=${condition.id}`)}
                                    title="問診票を編集"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </TableCell>
                              <TableCell className="py-4">
                                <div className="flex gap-2 justify-center">
                                  <button
                                    type="button"
                                    onClick={() => handleEdit(condition)}
                                    aria-label="編集"
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDuplicate(condition)}
                                    title="複製"
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(condition)}
                                    disabled={!canDelete(condition)}
                                    title={canDelete(condition) ? '削除' : '同じ性別×年齢の最後の1件は削除できません'}
                                    className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border ${canDelete(condition) ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-gray-100 text-gray-300 cursor-not-allowed'}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    </div>
                  );
                })()}

                {/* ページネーション */}
              {data?.pagination && (
                <div className="flex justify-between items-center mt-5 pt-4">
                  <div className="text-sm text-gray-500">
                    全 {data.pagination.total} 件中 {((page - 1) * 20 + 1)} - {Math.min(page * 20, data.pagination.total)} 件表示
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      前へ
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page * 20 >= data.pagination.total}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      次へ
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 単体セルテスト */}
        <CellTestPanel onEditCondition={async (conditionId) => {
          try {
            const res = await fetch(`/api/admin/conditions/${conditionId}`, { credentials: 'include' });
            if (res.ok) {
              const d = await res.json();
              if (d.condition) handleEdit(d.condition);
            }
          } catch {}
        }} />

        {/* カテゴリ一括テスト */}
        <CategoryTestPanel onEditCondition={async (conditionId) => {
          try {
            const res = await fetch(`/api/admin/conditions/${conditionId}`, { credentials: 'include' });
            if (res.ok) {
              const d = await res.json();
              if (d.condition) handleEdit(d.condition);
            }
          } catch {}
        }} />

        {/* 編集ダイアログ */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">健康心理条件編集</DialogTitle>
              <DialogDescription className="text-base">健康心理条件 (C×Lv テーブル) を編集します</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* 初診時医療面接モードフラグ: ONなら紐づく課題文の面接は全て初診時医療面接として扱う */}
              <div className="flex items-center justify-between rounded-md border bg-amber-50 px-4 py-3">
                <div>
                  <Label className="text-sm font-medium">初診時医療面接モード</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    ONにすると、この健康心理条件に紐づく課題文（シナリオ）の面接は全て初診時医療面接として扱われます。
                  </p>
                </div>
                <Switch checked={editIsIntake} onCheckedChange={setEditIsIntake} />
              </div>

              {editUseCsvMode ? (
                <>
                  {/* 基本情報 */}
                  <div className="mb-4 grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-csv-gender">性別</Label>
                      <Select value={editSelectedGender} onValueChange={setEditSelectedGender}>
                        <SelectTrigger id="edit-csv-gender" className="w-full">
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="男性">男性</SelectItem>
                          <SelectItem value="女性">女性</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="edit-csv-age_range">年齢層</Label>
                      <Select value={editSelectedAgeRange} onValueChange={setEditSelectedAgeRange}>
                        <SelectTrigger id="edit-csv-age_range" className="w-full">
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="20-34">20-34歳</SelectItem>
                          <SelectItem value="35-49">35-49歳</SelectItem>
                          <SelectItem value="50+">50歳以上</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* CSVデータテーブル(編集可能) */}
                  {selectedConditionGrid && (
                    <div className="mt-4 overflow-x-auto">
                      <Table className="min-w-full">
                        <TableHeader>
                          <TableRow>
                            {selectedConditionGrid.headers.map((header: string, idx: number) => (
                              <TableHead key={idx} className="text-base font-bold text-gray-700 text-center whitespace-nowrap">{header}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedConditionGrid.rows.map((row: any, rowIdx: number) => (
                            <TableRow key={rowIdx}>
                              {selectedConditionGrid.headers.map((header: string, headerIdx: number) => {
                                const cellKey = `${rowIdx}::${header}`;
                                const value = editGridCells[cellKey] ?? '';
                                // ID/カテゴリ は読み取り専用、それ以外 (Lv1..Lv6 / 備考) は編集可
                                const isReadOnly = header === 'ID' || header === 'カテゴリ';
                                if (isReadOnly) {
                                  return (
                                    <TableCell key={headerIdx} className="text-base text-gray-900 text-center whitespace-nowrap font-medium">
                                      {value || '-'}
                                    </TableCell>
                                  );
                                }
                                return (
                                  <TableCell key={headerIdx} className="align-top p-2 min-w-[140px]">
                                    <Textarea
                                      value={value}
                                      onChange={(e) => setEditGridCells((prev) => ({ ...prev, [cellKey]: e.target.value }))}
                                      placeholder="-"
                                      rows={2}
                                      className="text-sm rounded-lg border-gray-200 bg-white min-h-[44px]"
                                    />
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </>
              ) : editUseQuestionnaireFormMode ? (
                <>
                  {/* 基本情報 */}
                  <div className="mb-4 grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-questionnaire-gender">性別</Label>
                      <Select value={editSelectedGender} onValueChange={setEditSelectedGender}>
                        <SelectTrigger id="edit-questionnaire-gender" className="w-full">
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="男性">男性</SelectItem>
                          <SelectItem value="女性">女性</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="edit-questionnaire-age_range">年齢層</Label>
                      <Select value={editSelectedAgeRange} onValueChange={setEditSelectedAgeRange}>
                        <SelectTrigger id="edit-questionnaire-age_range" className="w-full">
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="20-34">20-34歳</SelectItem>
                          <SelectItem value="35-49">35-49歳</SelectItem>
                          <SelectItem value="50+">50歳以上</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* 問診票フォームは廃止 — categoryTable で管理 */}
                </>
              ) : (
                <>
                  {/* 基本情報 */}
                  <div className="mb-4 grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-gender">性別</Label>
                      <Select value={editSelectedGender} onValueChange={setEditSelectedGender}>
                        <SelectTrigger id="edit-gender" className="w-full">
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="男性">男性</SelectItem>
                          <SelectItem value="女性">女性</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="edit-age_range">年齢層</Label>
                      <Select value={editSelectedAgeRange} onValueChange={setEditSelectedAgeRange}>
                        <SelectTrigger id="edit-age_range" className="w-full">
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="20-34">20-34歳</SelectItem>
                          <SelectItem value="35-49">35-49歳</SelectItem>
                          <SelectItem value="50+">50歳以上</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* 動的フォーム */}
                  <div className="mt-4">
                    <Tabs defaultValue="dental-history" className="w-full">
                      <TabsList className="grid w-full grid-cols-5 bg-gray-100">
                        <TabsTrigger 
                          value="dental-history"
                          className="data-[state=active]:bg-medical-blue data-[state=active]:text-white data-[state=active]:font-semibold"
                        >
                          歯科歴
                        </TabsTrigger>
                        <TabsTrigger 
                          value="lifestyle"
                          className="data-[state=active]:bg-medical-blue data-[state=active]:text-white data-[state=active]:font-semibold"
                        >
                          生活習慣
                        </TabsTrigger>
                        <TabsTrigger 
                          value="smoking"
                          className="data-[state=active]:bg-medical-blue data-[state=active]:text-white data-[state=active]:font-semibold"
                        >
                          喫煙
                        </TabsTrigger>
                        <TabsTrigger 
                          value="oral-hygiene"
                          className="data-[state=active]:bg-medical-blue data-[state=active]:text-white data-[state=active]:font-semibold"
                        >
                          口腔衛生
                        </TabsTrigger>
                        <TabsTrigger 
                          value="systemic-disease"
                          className="data-[state=active]:bg-medical-blue data-[state=active]:text-white data-[state=active]:font-semibold"
                        >
                          全身疾患
                        </TabsTrigger>
                      </TabsList>

                      {Object.keys(editDynamicFormTabs).map((tabKey) => (
                        <TabsContent key={tabKey} value={tabKey} className="space-y-4 mt-4">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">質問と回答</h3>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // 新しい質問を追加
                                const newQuestion = '新しい質問';
                                const newItem: ConditionFormItem = {
                                  key: newQuestion, // 一時的なキーとして使用
                                  label: newQuestion,
                                  placeholder: '回答を入力してください',
                                };
                                setEditDynamicFormTabs({
                                  ...editDynamicFormTabs,
                                  [tabKey]: [...editDynamicFormTabs[tabKey], newItem],
                                });
                                setEditConditionFormData({
                                  ...editConditionFormData,
                                  [newQuestion]: '',
                                });
                              }}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              追加
                            </Button>
                          </div>
                          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                            {editDynamicFormTabs[tabKey].map((item) => (
                              <div key={item.key} className={`space-y-2 p-4 border rounded-lg ${item.disabled ? 'opacity-50 pointer-events-none bg-gray-50' : 'bg-white'}`}>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 space-y-2">
                                    <div>
                                      <Label htmlFor={`edit-form-question-${item.key}`} className={item.disabled ? 'text-gray-400' : ''}>
                                        質問
                                      </Label>
                                      <Input
                                        id={`edit-form-question-${item.key}`}
                                        value={item.label}
                                        onChange={(e) => {
                                          const newLabel = e.target.value;
                                          const updatedTabs = { ...editDynamicFormTabs };
                                          const oldKey = item.key;
                                          const newKey = newLabel.trim() || oldKey; // 空の場合は元のキーを使用
                                          
                                          // タブ内のkeyとlabelを更新
                                          updatedTabs[tabKey] = updatedTabs[tabKey].map((i) =>
                                            i.key === oldKey ? { ...i, key: newKey, label: newLabel } : i
                                          );
                                          setEditDynamicFormTabs(updatedTabs);
                                          
                                          // editConditionFormDataも更新
                                          if (oldKey !== newKey && editConditionFormData[oldKey] !== undefined) {
                                            const updatedFormData = { ...editConditionFormData };
                                            updatedFormData[newKey] = updatedFormData[oldKey];
                                            delete updatedFormData[oldKey];
                                            setEditConditionFormData(updatedFormData);
                                          }
                                        }}
                                        placeholder="質問を入力してください"
                                        disabled={item.disabled}
                                        className={item.disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
                                      />
                                    </div>
                                    <div>
                                      <Label htmlFor={`edit-form-answer-${item.key}`} className={item.disabled ? 'text-gray-400' : ''}>
                                        回答
                                      </Label>
                                      <Textarea
                                        id={`edit-form-answer-${item.key}`}
                                        value={editConditionFormData[item.key] || ''}
                                        onChange={(e) =>
                                          setEditConditionFormData({
                                            ...editConditionFormData,
                                            [item.key]: e.target.value,
                                          })
                                        }
                                        placeholder={item.placeholder}
                                        rows={2}
                                        disabled={item.disabled}
                                        className={item.disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
                                      />
                                    </div>
                                  </div>
                                  {!item.disabled && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const updatedTabs = { ...editDynamicFormTabs };
                                        updatedTabs[tabKey] = updatedTabs[tabKey].filter((i) => i.key !== item.key);
                                        setEditDynamicFormTabs(updatedTabs);
                                        const updatedFormData = { ...editConditionFormData };
                                        delete updatedFormData[item.key];
                                        setEditConditionFormData(updatedFormData);
                                      }}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                                {item.disabled && (
                                  <p className="text-xs text-gray-400 mt-1">(??????????????)</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </TabsContent>
                      ))}
                    </Tabs>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsEditDialogOpen(false);
                setEditConditionFormData({});
                setEditQuestionnaireFormData({});
                setEditSelectedGender('');
                setEditSelectedAgeRange('');
                setEditUseQuestionnaireFormMode(false);
                setEditUseCsvMode(false);
                setEditDynamicFormTabs(conditionFormTabs); // リセット
              }}>
                キャンセル
              </Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending} className="bg-blue-600 hover:bg-blue-700 text-white">
                {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '保存'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>削除確認</DialogTitle>
              <DialogDescription>
                この健康心理条件（ID: {selectedCondition?.id}）を削除してもよろしいですか？
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>キャンセル</Button>
              <Button onClick={confirmDelete} disabled={deleteMutation.isPending} className="bg-red-600 hover:bg-red-700 text-white">
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '削除'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
